/* eslint-disable jsx-a11y/media-has-caption */
import { Box, Container, Typography, Button } from '@mui/material';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HowItWorksDemo from '@modules/help/components/HowItWorksDemo';

const PRIMARY = 'rgb(11, 90, 240)';
const ACCENT = '#04c25d';

// Wrapper qui fait apparaître son contenu en fade + slide-up au scroll
const ScrollReveal = ({
  children,
  delay = 0,
  y = 24,
}: {
  children: React.ReactNode;
  delay?: number;
  y?: number;
}) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : `translateY(${y}px)`,
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </Box>
  );
};

const TESTIMONIALS_ROW_1 = [
  {
    text: 'Réactive sur les demandes, ça marche bien dès la première semaine pour moi.',
    name: 'Mathilde C.',
    job: 'Réflexologue · Tours',
  },
  {
    text: 'Enfin un outil simple qui me ramène des clients sans y passer des heures.',
    name: 'Anne-Sophie L.',
    job: 'Massage bien-être · Lyon',
  },
  {
    text: "Je ne pensais pas que c'était possible de remplir mon agenda sans agence.",
    name: 'Isabelle B.',
    job: 'Sophrologue · Rennes',
  },
  {
    text: "Les clients viennent à moi maintenant, je n'ai plus à courir après les rendez-vous.",
    name: 'Camille D.',
    job: 'Réflexologue · Bordeaux',
  },
  {
    text: "J'ai eu mon 1er rendez-vous à peine 3 jours après mon inscription.",
    name: 'Julie M.',
    job: 'Naturopathe · Nantes',
  },
];

const TESTIMONIALS_ROW_2 = [
  TESTIMONIALS_ROW_1[4],
  TESTIMONIALS_ROW_1[0],
  TESTIMONIALS_ROW_1[1],
  TESTIMONIALS_ROW_1[2],
  TESTIMONIALS_ROW_1[3],
];

interface LpSharedSectionsProps {
  /**
   * Callback déclenché à chaque clic CTA des sections partagées.
   * Le caller est responsable du tracking et de la navigation (avec forward des params d'URL).
   */
  onCtaClick: (source: string) => void;
}

/**
 * Sections 2 à 5 de la nouvelle landing page (réseau / mutualisation).
 *  - Section "Le principe" (mutualisation visibilité)
 *  - Démo interactive (HowItWorksDemo)
 *  - Témoignages (marquee)
 *  - CTA final (gradient)
 *
 * Utilisé par /lp et /lp/nouvelle-demande pour partager le bas de page.
 */
const LpSharedSections = ({ onCtaClick }: LpSharedSectionsProps) => {
  return (
    <>
      {/* ───────── SECTION — LE PRINCIPE (MUTUALISATION) ───────── */}
      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 10 }, pb: 0 }}>
        <ScrollReveal>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: PRIMARY,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              Le principe
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: '1.9rem', md: '2.8rem' },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
                mb: 3,
                color: '#0f172a',
              }}
            >
              Plus on est nombreux,{' '}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${PRIMARY} 0%, ${ACCENT} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                plus chacun y gagne.
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1.02rem', md: '1.18rem' },
                color: 'rgb(60, 60, 60)',
                maxWidth: 760,
                mx: 'auto',
                lineHeight: 1.65,
                mb: 3.5,
              }}
            >
              Le principe est simple. Plus on est nombreux, plus chacun a accès à des{' '}
              <Box component="strong" sx={{ color: '#0f172a', fontWeight: 700 }}>
                outils
              </Box>
              , des{' '}
              <Box component="strong" sx={{ color: '#0f172a', fontWeight: 700 }}>
                campagnes
              </Box>{' '}
              et des{' '}
              <Box component="strong" sx={{ color: '#0f172a', fontWeight: 700 }}>
                ressources
              </Box>{' '}
              qu&apos;un praticien isolé ne pourrait jamais financer seul.
            </Typography>

            {/* Chips d'exemples concrets */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 1,
                maxWidth: 640,
                mx: 'auto',
              }}
            >
              {[
                'Un site internet',
                'Des posts réseaux sociaux',
                'Des nouveaux clients',
                'Des avis Google',
                'Un CRM',
                'Des tutos',
              ].map((label) => (
                <Box
                  key={label}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 999,
                    bgcolor: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(11,90,240,0.18)',
                    color: PRIMARY,
                    fontSize: 13,
                    fontWeight: 600,
                    transition: 'transform 0.2s, box-shadow 0.2s, background 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      background: '#fff',
                      boxShadow: '0 6px 16px rgba(11,90,240,0.15)',
                    },
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
        </ScrollReveal>
      </Container>

      {/* ───────── SECTION — DÉMO INTERACTIVE ───────── */}
      <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 4, md: 8 } }}>
        <ScrollReveal y={32}>
          <HowItWorksDemo
            showComparison={false}
            cta={{
              label: "Je m'inscris gratuitement",
              engagementText: 'Inscription en 2 minutes · Sans carte bancaire',
              onClick: () => onCtaClick('demo'),
            }}
          />
        </ScrollReveal>
      </Container>

      {/* ───────── SECTION — TÉMOIGNAGES ───────── */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <ScrollReveal>
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: PRIMARY,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  mb: 2,
                }}
              >
                Témoignages
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: '1.8rem', md: '2.6rem' },
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                  mb: 2,
                  color: '#0f172a',
                }}
              >
                Des praticiens utilisent déjà le réseau.
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'rgb(100, 100, 100)',
                  maxWidth: 560,
                  mx: 'auto',
                }}
              >
                Les résultats varient selon les zones, les périodes et la réactivité.
              </Typography>
            </Box>
          </ScrollReveal>
        </Container>

        <Box
          sx={{
            overflow: 'hidden',
            maxWidth: '100%',
            '@keyframes marqueeLeft': {
              '0%': { transform: 'translateX(0)' },
              '100%': { transform: 'translateX(-50%)' },
            },
            '@keyframes marqueeRight': {
              '0%': { transform: 'translateX(-50%)' },
              '100%': { transform: 'translateX(0)' },
            },
          }}
        >
          {[
            { direction: 'marqueeLeft', items: TESTIMONIALS_ROW_1 },
            { direction: 'marqueeRight', items: TESTIMONIALS_ROW_2 },
          ].map((row, rowIndex) => (
            <Box
              key={rowIndex}
              sx={{
                display: 'flex',
                width: 'max-content',
                animation: `${row.direction} 40s linear infinite`,
                mb: rowIndex === 0 ? 2 : 0,
              }}
            >
              {[...row.items, ...row.items].map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    flexShrink: 0,
                    width: 320,
                    mx: 1,
                    p: 2.5,
                    bgcolor: 'white',
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.25, mb: 1 }}>
                    {[0, 1, 2, 3, 4].map((s) => (
                      <StarIcon key={s} sx={{ fontSize: 16, color: 'rgb(237, 156, 5)' }} />
                    ))}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontStyle: 'italic',
                      mb: 1.5,
                      lineHeight: 1.5,
                      color: '#1a1a1a',
                    }}
                  >
                    &quot;{item.text}&quot;
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'rgb(100, 100, 100)' }}>
                    {item.name} — {item.job}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        <Container maxWidth="lg" sx={{ mt: { xs: 5, md: 7 }, textAlign: 'center' }}>
          <ScrollReveal>
            <Button
              variant="contained"
              size="large"
              onClick={() => onCtaClick('testimonials')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: 15, md: 17 },
                py: { xs: 1.5, md: 1.8 },
                px: { xs: 4, md: 5 },
                borderRadius: 2,
                background: `linear-gradient(135deg, ${PRIMARY} 0%, #0a4ed1 100%)`,
                boxShadow: '0 8px 24px rgba(11,90,240,0.35)',
                '&:hover': {
                  background: `linear-gradient(135deg, #0a4ed1 0%, ${PRIMARY} 100%)`,
                  boxShadow: '0 12px 28px rgba(11,90,240,0.45)',
                },
              }}
            >
              Je m&apos;inscris gratuitement
            </Button>
            <Typography sx={{ fontSize: 13, color: 'rgb(100, 100, 100)', mt: 1.5 }}>
              Inscription en 2 minutes · Sans carte bancaire
            </Typography>
          </ScrollReveal>
        </Container>
      </Box>

      {/* ───────── SECTION — CTA FINAL ───────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <ScrollReveal y={36}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: 4,
              overflow: 'hidden',
              background: `linear-gradient(135deg, ${PRIMARY} 0%, #0a4ed1 60%, ${ACCENT} 100%)`,
              p: { xs: 4, md: 7 },
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(11,90,240,0.3)',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background:
                  'radial-gradient(ellipse at 20% 30%, rgba(255,255,255,0.15) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                pointerEvents: 'none',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: '1.8rem', md: '2.8rem' },
                  fontWeight: 800,
                  color: '#fff',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.15,
                  mb: 2,
                  maxWidth: 760,
                  mx: 'auto',
                }}
              >
                Rejoignez le réseau. Recevez vos premières demandes.
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  color: 'rgba(255,255,255,0.92)',
                  maxWidth: 580,
                  mx: 'auto',
                  mb: 4,
                  lineHeight: 1.55,
                }}
              >
                Inscription gratuite. Sans carte bancaire. Vous décidez ensuite si vous souhaitez
                aller plus loin.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => onCtaClick('final')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: 15, md: 17 },
                  py: { xs: 1.5, md: 1.8 },
                  px: { xs: 4, md: 5 },
                  borderRadius: 2,
                  bgcolor: '#fff',
                  color: PRIMARY,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    boxShadow: '0 12px 28px rgba(0,0,0,0.2)',
                  },
                }}
              >
                Je m&apos;inscris gratuitement
              </Button>

              {/* Social proof sous le CTA : photos + texte */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  mt: 3.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {[
                    'https://framerusercontent.com/images/iVqUKcOjEIhR2xg4c08qfwujSVg.png',
                    'https://framerusercontent.com/images/GLoPIOdIWtLKJcLTQRSPCcj5F0.png?width=611&height=920',
                    'https://framerusercontent.com/images/ciAFofBBGfNJSJHNtX1eS6vVYM.png',
                  ].map((src, i) => (
                    <Box
                      key={src}
                      sx={{
                        position: 'relative',
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '2px solid #fff',
                        ml: i === 0 ? 0 : '-10px',
                        bgcolor: '#fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }}
                    >
                      <Image
                        src={src}
                        alt="Praticien du réseau"
                        fill
                        sizes="36px"
                        style={{ objectFit: 'cover' }}
                      />
                    </Box>
                  ))}
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: '#d90b74',
                      display: 'grid',
                      placeItems: 'center',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 11,
                      ml: '-10px',
                      border: '2px solid #fff',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  >
                    +494
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    color: 'rgba(255,255,255,0.95)',
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    +494 praticiens ont déjà sécurisé leur place
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </ScrollReveal>
      </Container>
    </>
  );
};

export default LpSharedSections;
